import qs from 'qs'
import Cookies from 'js-cookie'
import i18next from 'i18next'
import type { i18n as I18Next } from 'i18next'
import type { Trans, TFunction, DefaultNamespace } from 'react-i18next'

import { Dict } from './dict'
import { clearTranslationSpace, clearSpace } from './utils';
import type { _Dict, Item } from './dict'
import type { Slot } from './slot'

declare global {
    interface Window {
        /** @deprecated */
        isIntlUser?: boolean
        
        /** @deprecated use window.intl (boolean) */
        userOriginFrom?: 'intl' | 'normal'
        
        intl: boolean
        
        language?: string
        
        country: {
            name: string
            code: string
        }
        
        cdnHosts: string[]
    }
}

export type Language = 'zh' | 'en' | 'ja' | 'ko' | 'pt' | 'ind'

export const LANGUAGES = ['zh', 'en', 'ja', 'ko', 'pt', 'ind'] as const

/**
    提供翻译文本功能，在浏览器环境下自动解析当前语言、国际/国内用户
    @see https://git.woa.com/TCBTeam/i18n
*/
export class I18N {
    static LANGUAGE_REGEXP = /^(zh|en|ja|jp|ko|pt|ind)$/
    
    
    /** (ISO 639-1 标准语言代码) 当前为国际站时可能取 zh, en, ja, ko, pt, ind;  当前为国内站时可能取 zh, en （当前站点可通过 i18n.intl (boolean) 或者 window.intl (boolean) 判断） */
    language: Language
    
    /** (非 ISO 639-1 标准语言代码) 当前为国际站时可能取 zh, en, jp, ko, pt, ind;  当前为国内站时可能取 zh, en （当前站点可通过 i18n.intl (boolean) 或者 window.intl (boolean) 判断） */
    get lang (): 'zh' | 'en' | 'jp' | 'ko' | 'pt' | 'ind' {
        return this.language === 'ja' ? 'jp' : this.language
    }
    
    set lang (language: 'zh' | 'en' | 'jp' | 'ko' | 'pt' | 'ind') {
        this.language = language === 'jp' ? 'ja' : language
    }
    
    /** (非 ISO 639-1 标准语言代码) 当前为国际站时可能取 zh, en, jp, ko;  当前为国内站时可能取 zh, en （当前站点可通过 i18n.intl (boolean) 或者 window.intl (boolean) 判断） */
    get lng (): 'zh' | 'en' | 'jp' | 'ko' | 'pt' | 'ind' {
        return this.language === 'ja' ? 'jp' : this.language
    }
    
    set lng (language: 'zh' | 'en' | 'jp' | 'ko' | 'pt' | 'ind') {
        this.language = language === 'jp' ? 'ja' : language
    }
    
    
    /** @deprecated [仅 qcbuy 中有, 应统一使用 language] 当前为国际站时 language === intl_language；当前为国内站时可能 language !== intl_language，因为目前国内站遗留业务不支持 language 为 ja, ko */
    intl_language: Language
    
    /** 当前是否为国际站  
        (一般情况下会自动判断，手动修改该属性后请调用 i18n.set_hosts() 否则 i18n.hosts, i18n.roots 不会更新)  
    */
    intl: boolean = false
    
    
    /** 当前域名后缀 */
    suffix: 'com' | 'cn' | 'com.cn' = 'com'

    /** 当前域名前缀*/
    prefix: 'cloud.tencent.' | 'tencentcloud.';
    
    /** 注册国家 http://tapd.oa.com/QCloud_2015/markdown_wikis/view/#1010103951009041637  
        @example { name: 'CN', code: '86' }  
        (在 shell 中被设置)  
    */
    country: {
        /** 国家名称 ISO2 标准 */
        name: string
        
        /** 国家编号 */
        code: string
    } = {
        name: 'CN',
        code: '86'
    }
    
    /** @deprecated 建议使用 intl */
    get site () {
        return this.intl ? 2 : 1
    }
    
    /** @deprecated use i18n.intl (boolean) */
    get userOriginFrom (): 'intl' | 'normal' {
        return this.intl ? 'intl' : 'normal'
    }
    
    set userOriginFrom (origin: 'intl' | 'normal') {
        this.intl = origin === 'intl'
    }
    
    
    /** hostname shortcuts
        
        @example
        if (i18n.intl)  // 国际站
            i18n.hosts.buy === 'buy.intl.cloud.tencent.com' 或 'buy.intl.cloud.tencent.cn'
        else  // 国内站
            i18n.hosts.buy === 'buy.cloud.tencent.com' 或 'buy.cloud.tencent.cn'
    */
    hosts: Hosts
    
    /** url prefix shortcuts
        
        @example
        if (i18n.intl)  // 国际站
            i18n.roots.buy === 'https://buy.intl.cloud.tencent.com' 或 'https://buy.intl.cloud.tencent.cn'
        else  // 国内站
            i18n.roots.buy === 'https://buy.cloud.tencent.com' 或 'https://buy.cloud.tencent.cn'
    */
    roots: Roots
    
    /** @deprecated use i18n.hosts.cdn / i18n.roots.cdn */
    cdnHost?: string
    
    /** @deprecated use i18n.hosts / i18n.roots */
    HOST_CONFIG?: HOST_CONFIG
    
    
    /** 标记静态文本，以便扫描词条，并在运行时根据当前语言获取翻译 */
    t : (text: string, options?: { language?: Language, context?: string, count?: number, [key: string]: any }) => string
    
    /** 标记静态文本，以便扫描词条，并在运行时根据当前语言获取翻译 */
    __: (text: string, options?: { language?: Language, context?: string, count?: number, [key: string]: any }) => string
    
    
    /** render: 翻译配置字段 */
    r : (field: Item | undefined | null) => string
    
    /** @deprecated  兼容 qcbuy 递归翻译对象 key 以及 value */
    __rt: <T>(object: T) => T
    
    
    i18next: I18Next
    
    /** react-i18next <Trans/> 组件 */
    Trans: typeof Trans = ({ children }) => children as any
    
    Slot: typeof Slot = ({ content }) => content as any
    
    
    /** ```ts
        import dict from './dict.json'  // { "添加": { "en": "Add", "ja": "追加", "ko": "추가" } }
        
        const i18n = new I18N(dict, 'zh')  // 创建实例，传入词典 dict 并指定语言（NodeJS 环境），
        const i18n = new I18N(dict)        // 创建实例，传入词典 dict 并自动判断当前语言（浏览器环境），
        const i18n = new I18N({ })         // 创建实例，传入空词典
        ```
        @see https://git.woa.com/TCBTeam/i18n
    */
    constructor (_dict: _Dict, language?: Language) {
        const is_browser = typeof document !== 'undefined' && typeof window !== 'undefined'
        
        const dict = new Dict(clearTranslationSpace(_dict))
        
        // --- if in bowser then detect language & intl
        if (is_browser) {
            const { hostname = '', search = '' } = document.location || {}
            const { intl: wintl, userOriginFrom, isIntlUser, cdnHosts } = window
            const queries = qs.parse(search, { ignoreQueryPrefix: true })
            // 新域名支持变更点
            const tokens = /(cloud\.tencent\.|tencentcloud\.)(com|cn|com\.cn)$/.exec(hostname)
            
            this.intl = (() => {
                // 根据参数、域名判断
                if (queries.intl === '0') return false

                // 新域名扩展点
                if (
                    queries.intl === '1' ||
                    hostname.includes('intl.cloud.tencent.') ||
                    hostname.includes('tencentcloud.')
                ) return true
                
                // 根据服务端结果判断
                if (typeof wintl !== 'undefined') return wintl
                if (typeof userOriginFrom !== 'undefined') return userOriginFrom === 'intl'
                if (typeof isIntlUser !== 'undefined') return Boolean(isIntlUser)
                
                // 官网根据 getUserArea 接口 http://tapd.oa.com/QCloud_2015/markdown_wikis/view/#1010103951009041637  设置的 cookie，1 中国  2 国际，未登录时为空
                return Cookies.get('intl') === '2'
            })()
            
            if (!language) {
                const lquery   = (queries.language || queries.lang) as string  // 暂时不考虑是数组的情况
                const lwindow  = window.language
                const lbrowser = typeof navigator !== 'undefined' && navigator.language.slice(0, 2)
                
                language = (
                    this.intl ? 
                        lquery || lwindow || Cookies.get('language') || lbrowser || 'en'
                    :
                        ((lquery || lwindow || Cookies.get('language') || lbrowser) === 'en') ? 'en' : 'zh'
                ) as Language
            }
            
            if (!I18N.LANGUAGE_REGEXP.test(language))
                language = this.intl ? 'en' : 'zh'
            
            // console.log(`intl = ${this.intl}, language = ${language}`)
            
            this.set_hosts({
                suffix: tokens?.[2] as 'com' | 'cn' | 'com.cn',
                cdns: cdnHosts,
                prefix: tokens?.[1] as 'cloud.tencent.' | 'tencentcloud.'
            })
        } else {
            this.set_hosts()
        }
        
        this.language = language = ((language as string) === 'jp' ? 'ja' : language) || 'zh'
    
        this.t = this.__ = (text, options) => {
            options = options || { }
            
            const language = options.language || this.language
            
            return this.i18next.t(clearSpace(text), { ...options, lng: language, defaultValue: text })
        } 
        
        this.r = (field) => 
            field ?
                field[this.language] || field.en || field.zh || field as any || ''
            :
                field || ''
        
        
        this.__rt = <T>(obj: T) => {
            if (!obj) return obj
            if (typeof obj === 'string') return this.__(obj) as unknown as T
            if (typeof obj !== 'object') return obj as T
            
            return Object.entries(obj).reduce( (acc, [key, value]) => {
                acc[this.__(key)] = this.__rt(value)
                return acc
            }, { }) as T
        }
        
        
        // --- init i18next
        this.i18next = i18next.createInstance()
        
        if (is_browser) {
            try {
                // 在无 React 的浏览器环境下避免 react-i18next 中执行 React.createContext() 报错
                // const React = require('react') as typeof import('react')
                const { initReactI18next, Trans: I18NextTrans } = require('react-i18next') as typeof import('react-i18next')
                this.i18next.use(initReactI18next)
                const _i18next = this.i18next
                const _t = this.t
                // 绑定 Trans 组件的 i18n 到 this.i18next, 解决多个 i18next 冲突的问题
                // react-i18next/context.js 中 i18n 实例只在模块级别维护，多次 this.i18next.use(initReactI18next) 会覆盖前面的 i18n，导致 Trans 无法翻译
                // https://github.com/i18next/react-i18next/issues/726
                this.Trans = function Trans ({ i18n = _i18next, ...others }) {
                    // 简单转发，性能更好
                    // @ts-ignore
                    return I18NextTrans<never, DefaultNamespace, HTMLDivElement >({ i18n, t: _t, ...others })
                    // return React.createElement(I18NextTrans, { i18n, ...others } as any, children)
                    // return <I18NextTrans {...{ i18n, ...others } }>{children}</I18NextTrans>
                }
                this.Slot = require('./slot').default
            } catch { }
        }
        
        this.i18next.init({
            lng: this.language,
            // LOCAL
            // debug: true,
            debug: false,
            fallbackLng: {
                en: ['zh'],
                ja: ['en', 'zh'],
                ko: ['en', 'zh'],
                pt: ['en', 'zh'],
                ind: ['en', 'zh'],
            },
            // 禁用 : 和 . 作为 seperator
            keySeparator: false,
            nsSeparator: false,
            resources: dict.to_resources(),
            interpolation: {
                escapeValue: false
            },
            react: {
                transKeepBasicHtmlNodesFor: []
            },
        })
        
        // TODO: i18n实例可能在tcb_buy\tcb-component\某些业务代码，即存在window上抢注的风险，目前加载顺序刚好是tcb_buy最先，此处逻辑挪至tcb_buy中较合适，移除时会不会有其他项目旧版本含有此逻辑导致覆盖问题？
        if (typeof window !== 'undefined' && window && !('i18n' in window))
            (window as any).i18n = this
    }
    
    
    /** 加载词典文件 (需要将这两行单独放一个文件里，以保证在 import 其他文件之前执行)  
        
        @example
        import dict from './dict.json'  // { "添加": { "en": "Add", "ja": "追加", "ko": "추가" } }
        i18n.init(dict)
    */
    init (dict: _Dict) {
        Object.entries(
            new Dict(dict).to_resources()
        ).forEach(([language, { translation }]) => {
            this.addResources(language, 'translation', translation)
        })
        
        // tea 词条
        // if (translation && typeof translation === 'object')
        //     this.i18next.addResources(this.language, 'translation', translation)
    }

    addResources(lng: string, ns: string, resources: any){
        this.i18next.addResources(lng, ns, clearTranslationSpace(resources))
    }

    addResourceBundle(lng: string, ns: string, resources: any, deep?: boolean, overwrite?: boolean){
        this.i18next.addResourceBundle(lng, ns, clearTranslationSpace(resources), deep, overwrite)
    }
    
    toJSON () {
        return {
            language: this.language,
            userOriginFrom: this.userOriginFrom
        }
    }
    
    
    /** 更新 hosts 和 roots */
    set_hosts ({
        suffix = this.suffix,
        cdns = this.hosts?.cdns || [`cloudcache.tencent-cloud.${ this.suffix === 'com' ? 'com' : 'cn' }`, 'imgcache.qq.com', 'cloudcache.tencentcs.cn'],
        prefix = 'cloud.tencent.'
    }: {
        suffix?: 'com' | 'cn' | 'com.cn'
        cdns?: string[],
        prefix?: 'cloud.tencent.' | 'tencentcloud.'
    } = { }) {
        if (!['com', 'cn', 'com.cn'].includes(suffix))
            suffix = 'com'
        
        this.suffix = suffix
        this.prefix = prefix
        
        if (!cdns?.[0])
            cdns = [`cloudcache.tencent-cloud.${ this.suffix === 'com' ? 'com' : 'cn' }`, 'imgcache.qq.com', 'cloudcache.tencentcs.cn']

        let cloud = 'cloud.tencent.com';

        if (this.intl) {
            cloud = prefix === 'cloud.tencent.' ? `intl.${prefix}${suffix}` : `${prefix}${suffix}`
        } else {
            cloud = `${prefix}${suffix}`
        }

        this.hosts = {
            cloud,
            buy: `buy.${cloud}`,
            console: `console.${cloud}`,
            cdn: cdns[0] as any,
            cdns,
        }
        
        this.roots = {
            cloud  : `https://${this.hosts.cloud}`,
            buy    : `https://${this.hosts.buy}`,
            console: `https://${this.hosts.console}`,
            cdn    : `https://${this.hosts.cdn}`,
            login  : `https://${this.hosts.cloud}/login`,
            register: `https://${this.hosts.cloud}/register`
        } as Roots
        
        // 保持兼容
        this.HOST_CONFIG = {
            base: cloud,
            qcbuy: this.hosts.buy,
            console: this.hosts.console,
            qcmain: cloud,
            cdnHost: this.hosts.cdn,
            fromOldHost: false
        }
        
        this.cdnHost = this.hosts.cdn
    }
    
    
    static MONTHS_EN = {
        1: 'January',
        2: 'February',
        3: 'March',
        4: 'April',
        5: 'May',
        6: 'June',
        7: 'July',
        8: 'August',
        9: 'September',
        10: 'October',
        11: 'November',
        12: 'December'
    }
    
    /** @deprecated  兼容 qcbuy 翻译数字月份为英文表示 */
    __monthEn (month: number | string): string {
        return I18N.MONTHS_EN[month] || month
    }
}



/** @deprecated use i18n.hosts / i18n.roots */
export interface HOST_CONFIG {
    base: `${ 'intl.' | '' }cloud.tencent.${ 'com' | 'cn' | 'com.cn' }` | `tencentcloud.com` | string
    qcbuy: `buy.${ 'intl.' | '' }cloud.tencent.${ 'com' | 'cn' | 'com.cn' }` | `buy.tencentcloud.com` | string
    console: `console.${ 'intl.' | '' }cloud.tencent.${ 'com' | 'cn' | 'com.cn' }` | `console.tencentcloud.com` | string
    qcmain: `${ 'intl.' | '' }cloud.tencent.${ 'com' | 'cn' | 'com.cn' }` | `tencentcloud.com` | string
    cdnHost: 'cloudcache.tencent-cloud.com' | 'cloudcache.tencent-cloud.cn' | 'cloudcache.tencentcs.cn'
    fromOldHost: false
}


export interface Hosts {
    cloud  : `${ 'intl.' | '' }cloud.tencent.${ 'com' | 'cn' | 'com.cn' }` | `tencentcloud.com` | string
    buy    : `buy.${ 'intl.' | '' }cloud.tencent.${ 'com' | 'cn' | 'com.cn' }` | `buy.tencentcloud.com` | string
    console: `console.${ 'intl.' | '' }cloud.tencent.${ 'com' | 'cn' | 'com.cn' }` | `console.tencentcloud.com` | string
    cdn    : 'cloudcache.tencent-cloud.com' | 'cloudcache.tencent-cloud.cn' | 'cloudcache.tencentcs.cn'
    cdns   : string[]
}


export interface Roots {
    cloud  : `https://${Hosts['cloud']}`
    buy    : `https://${Hosts['buy']}`
    console: `https://${Hosts['console']}`
    cdn    : `https://${Hosts['cdn']}`
    login  : `https://${Hosts['cloud']}/login`
    register: `https://${Hosts['cloud']}/register`
}

export type { _Dict, Item }

export interface I18NBasic {
    intl: boolean
    language: Language
}

export default I18N
